"use client";

import { cn } from "@/lib/utils";
import { type HTMLMotionProps, motion } from "framer-motion";
import React from "react";

interface ShinyButtonProps extends HTMLMotionProps<"button"> {
  children: React.ReactNode;
  className?: string;
  secondaryClassName?: string;
  variant?: "dark" | "light";
}

const animationProps: HTMLMotionProps<"button"> = {
  initial: { "--x": "100%", scale: 0.8 },
  animate: { "--x": "-100%", scale: 1 },
  whileTap: { scale: 0.95 },
  transition: {
    repeat: Number.POSITIVE_INFINITY,
    repeatType: "loop",
    repeatDelay: 1,
    type: "spring",
    stiffness: 20,
    damping: 15,
    mass: 2,
    scale: {
      type: "spring",
      stiffness: 200,
      damping: 5,
      mass: 0.5,
    },
  },
};

export const ShinyButton = React.forwardRef<
  HTMLButtonElement,
  ShinyButtonProps
>(
  (
    { children, className, secondaryClassName, variant = "dark", ...props },
    ref,
  ) => {
    const isDark = variant === "dark";

    return (
      <motion.button
        ref={ref}
        {...animationProps}
        {...props}
        className={cn(
          "relative rounded-lg px-6 py-2 font-bold backdrop-blur-xl transition-shadow duration-300 ease-in-out hover:shadow-lg uppercase tracking-wide cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none",
          isDark ? "bg-gray-900" : "bg-white",
          className,
        )}
      >
        <span
          className={cn(
            "relative inline-flex items-center justify-center gap-2 text-sm uppercase tracking-wide",
            isDark ? "text-[#00E266]" : "text-gray-900",
            secondaryClassName,
          )}
          style={{
            maskImage:
              "linear-gradient(-75deg, white calc(var(--x) + 20%), transparent calc(var(--x) + 30%), white calc(var(--x) + 100%))",
          }}
        >
          {children}
        </span>
        <span
          style={{
            mask: "linear-gradient(rgb(0,0,0), rgb(0,0,0)) content-box,linear-gradient(rgb(0,0,0), rgb(0,0,0))",
            maskComposite: "exclude",
          }}
          className={cn(
            "absolute inset-0 z-10 block rounded-[inherit] p-px",
            isDark
              ? "bg-[linear-gradient(-75deg,rgba(255,255,255,0.1)_calc(var(--x)+20%),rgba(255,255,255,0.5)_calc(var(--x)+25%),rgba(255,255,255,0.1)_calc(var(--x)+30%))]"
              : "bg-[linear-gradient(-75deg,rgba(0,226,102,0.1)_calc(var(--x)+20%),rgba(0,226,102,0.5)_calc(var(--x)+25%),rgba(0,226,102,0.1)_calc(var(--x)+30%))]",
          )}
        ></span>
      </motion.button>
    );
  },
);

ShinyButton.displayName = "ShinyButton";
